import { splitEvery } from 'ramda';
import { IntlShape } from 'react-intl';

import { readPurchases } from 'apis/enterprise/api';
import { localize } from 'common/utils';

import { getIntervalForPeriod } from './getIntervalForPeriod';
import { PurchaseWithBrandName, VenueWithBrandName, Period, DateRange } from '../types';

const MAX_CONCURRENT_REQUESTS = 5;
const MAX_PURCHASES_PER_REQUEST = 500;

export async function getLocalizedPurchases(
  venues: VenueWithBrandName[],
  selectedVenueIds: string[],
  downloadPeriod: Period,
  dateRange: DateRange,
  intl: IntlShape,
) {
  const allPurchases: PurchaseWithBrandName[] = [];
  const venuesToDownload = venues.filter(venue => selectedVenueIds.includes(venue.id));
  const [{ timezone }] = venuesToDownload;
  const interval = getIntervalForPeriod(downloadPeriod, timezone, dateRange);

  const venueChunks = splitEvery(MAX_CONCURRENT_REQUESTS, venuesToDownload);
  for (const venueChunk of venueChunks) {
    const deliveredPurchasesChunk = await Promise.all(
      venueChunk.map(venue =>
        readPurchases('delivered', {
          venueId: venue.id,
          startTime: interval.start.toISO(),
          endTime: interval.end.toISO(),
          limit: MAX_PURCHASES_PER_REQUEST,
        }),
      ),
    );
    deliveredPurchasesChunk.map(res => allPurchases.push(...res));
    const rejectedPurchasesChunk = await Promise.all(
      venueChunk.map(venue =>
        readPurchases('rejected', {
          venueId: venue.id,
          startTime: interval.start.toISO(),
          endTime: interval.end.toISO(),
          limit: MAX_PURCHASES_PER_REQUEST,
        }),
      ),
    );
    rejectedPurchasesChunk.map(res => allPurchases.push(...res));
  }
  const localizedPurchases: PurchaseWithBrandName[] = allPurchases.map(purchase => {
    const venue = venuesToDownload.find(venue => venue.id === purchase.restaurant.id);
    return {
      ...purchase,
      brandName: venue?.brandName,
      restaurant: {
        ...purchase.restaurant,
        name: venue ? localize(venue.name, intl.locale) : purchase.restaurant.name,
      },
    };
  });
  return localizedPurchases;
}
